import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  TrendingUp, ShoppingBag, DollarSign, Receipt, Package,
  PieChart, Table2, FileText, Ban, CreditCard,
  Search, RefreshCw, ChevronLeft, ChevronRight,
  X, Loader2, Banknote, Smartphone, SplitSquareHorizontal,
  AlertTriangle, Star, Zap, Download, ChevronDown,
  CheckCircle, TableProperties
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/report.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';

/* ─────────────────────────────────────────────
   API
───────────────────────────────────────────── */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${token}` } });
};

/* ─────────────────────────────────────────────
   IST DATE FORMATTER
───────────────────────────────────────────── */
const fmtIST = (date, short = false) => {
  if (!date) return '—';
  const d = new Date(date);
  if (short) {
    return d.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
    });
  }
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const PERIODS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week',      label: '7 Days' },
  { id: 'month',     label: '30 Days' },
  { id: 'year',      label: 'Year' },
  { id: 'custom',    label: 'Custom' },
];

const STATUS_META = {
  placed:    { label: 'Placed',    cls: 'placed' },
  kot:       { label: 'KOT',       cls: 'kot' },
  preparing: { label: 'Preparing', cls: 'preparing' },
  ready:     { label: 'Ready',     cls: 'ready' },
  served:    { label: 'Served',    cls: 'served' },
  cancelled: { label: 'Cancelled', cls: 'cancelled' },
  cart:      { label: 'Cart',      cls: 'cart' },
};

const fmt  = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtD = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (n) => parseInt(n || 0).toLocaleString('en-IN');
const pct  = (a, b) => b > 0 ? ((parseFloat(a) / parseFloat(b)) * 100).toFixed(1) : '0.0';

const NAV_ITEMS = [
  { id: 'sales',         label: 'Sales Report',     icon: <TrendingUp size={15}/> },
  { id: 'orders',        label: 'All Orders',        icon: <ShoppingBag size={15}/> },
  { id: 'items',         label: 'Item Sales',        icon: <Package size={15}/> },
  { id: 'tables',        label: 'Table Performance', icon: <TableProperties size={15}/> },
  { id: 'payments',      label: 'Payments',          icon: <CreditCard size={15}/> },
  { id: 'gst',           label: 'GST / Tax',         icon: <Receipt size={15}/> },
  { id: 'cancellations', label: 'Cancellations',     icon: <Ban size={15}/> },
];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useFilters() {
  const [period, setPeriod] = useState('today');
  const [from,   setFrom]   = useState('');
  const [to,     setTo]     = useState('');
  const params = useMemo(() => {
    const p = { period };
    if (period === 'custom' && from && to) { p.from = from; p.to = to; }
    return p;
  }, [period, from, to]);
  return { period, setPeriod, from, setFrom, to, setTo, params };
}

function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
}

/* ─────────────────────────────────────────────
   EXPORT BUTTON
───────────────────────────────────────────── */
function ExportBtn({ endpoint, params, filename }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const doExport = async (format) => {
    setOpen(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('restaurant_token');
      const p = new URLSearchParams({ ...params, format });
      const res = await fetch(`${baseurl}${endpoint}?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${filename}-${params.period || 'report'}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="rp-export-wrap">
      <button className="rp-btn primary" onClick={() => setOpen(v => !v)} disabled={loading}>
        {loading
          ? <Loader2 size={13} className="rp-spin"/>
          : <Download size={13}/>
        }
        Export
        <ChevronDown size={11}/>
      </button>
      {open && (
        <div className="rp-export-menu">
          <button className="rp-export-item" onClick={() => doExport('xlsx')}>
            <FileText size={13}/> Excel (.xlsx)
          </button>
          <button className="rp-export-item" onClick={() => doExport('csv')}>
            <FileText size={13}/> CSV (.csv)
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PERIOD BAR
───────────────────────────────────────────── */
function PeriodBar({ period, setPeriod, from, setFrom, to, setTo, onRefresh, loading, extra, exportProps }) {
  return (
    <div className="rp-header-right">
      <div className="rp-period-pills">
        {PERIODS.map(p => (
          <button key={p.id} className={`rp-period-btn ${period === p.id ? 'active' : ''}`}
            onClick={() => setPeriod(p.id)}>{p.label}</button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="rp-date-range">
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <span>→</span>
          <input type="date" value={to}   onChange={e => setTo(e.target.value)} />
        </div>
      )}
      {extra}
      <button className="rp-btn" onClick={onRefresh} disabled={loading}>
        {loading ? <Loader2 size={13} className="rp-spin"/> : <RefreshCw size={13}/>} Refresh
      </button>
      {exportProps && <ExportBtn {...exportProps} />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   BAR CHART
───────────────────────────────────────────── */
function BarChart({ data = [], valueKey = 'revenue', labelKey = 'date', maxBars = 14, height = 130 }) {
  const slice  = data.slice(-maxBars);
  const maxVal = Math.max(...slice.map(d => parseFloat(d[valueKey] || 0)), 1);
  return (
    <div className="rp-bar-chart" style={{ height }}>
      {slice.map((d, i) => {
        const val = parseFloat(d[valueKey] || 0);
        const h   = Math.max((val / maxVal) * 88, val > 0 ? 4 : 1);
        const lbl = labelKey === 'date'
          ? (d[labelKey] ? new Date(d[labelKey]).toLocaleDateString('en-IN', { day:'2-digit', month:'short', timeZone:'Asia/Kolkata' }) : i)
          : (d[labelKey] || i);
        return (
          <div key={i} className="rp-bar-wrap">
            <div className="rp-bar" style={{ height: `${h}%` }}>
              <div className="rp-bar-tooltip">{fmt(val)}</div>
            </div>
            <span className="rp-bar-label" style={{ position: 'static', marginTop: 4 }}>{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   DONUT CHART
───────────────────────────────────────────── */
function DonutChart({ segments = [], centerVal, centerLabel }) {
  const total = segments.reduce((s, x) => s + parseFloat(x.value || 0), 0);
  let cumDeg  = 0;
  const gradient = total > 0
    ? segments.map(seg => {
        const deg  = (parseFloat(seg.value) / total) * 360;
        const from = cumDeg;
        cumDeg += deg;
        return `${seg.color} ${from}deg ${cumDeg}deg`;
      }).join(', ')
    : 'var(--ink-4) 0deg 360deg';

  return (
    <div className="rp-donut-wrap">
      <div className="rp-donut" style={{ background: `conic-gradient(${gradient})` }}>
        <div className="rp-donut-hole">
          <div className="rp-donut-center-val">{centerVal}</div>
          <div className="rp-donut-center-label">{centerLabel}</div>
        </div>
      </div>
      <div className="rp-legend">
        {segments.map((seg, i) => (
          <div key={i} className="rp-legend-item">
            <div className="rp-legend-dot" style={{ background: seg.color }}/>
            <span className="rp-legend-name">{seg.label}</span>
            <span className="rp-legend-val">{fmt(seg.value)}</span>
            <span className="rp-legend-pct">{pct(seg.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER DETAIL DRAWER
───────────────────────────────────────────── */
function OrderDrawer({ orderId, onClose }) {
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    api().get(`/reports/orders/${orderId}`)
      .then(r => setOrder(r.data.order))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const items = useMemo(() => {
    if (!order) return [];
    try { return typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []); }
    catch { return []; }
  }, [order]);

  return (
    <div className="rp-drawer-overlay" onClick={onClose}>
      <div className="rp-drawer" onClick={e => e.stopPropagation()}>
        <div className="rp-drawer-header">
          <div className="rp-drawer-title">
            {loading ? 'Loading…' : order ? `Order #${order.order_number}` : 'Order'}
          </div>
          <button className="rp-drawer-close" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="rp-drawer-body">
          {loading && <div className="rp-loading"><Loader2 size={24} className="rp-spin"/></div>}
          {!loading && !order && <div className="rp-empty"><FileText size={32}/><p>Order not found</p></div>}
          {!loading && order && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className={`rp-badge ${STATUS_META[order.status]?.cls || ''}`}>
                  {STATUS_META[order.status]?.label || order.status}
                </span>
                <span className={`rp-pay-badge ${order.payment_method}`}>
                  {order.payment_method?.toUpperCase()}
                </span>
                {order.order_source && (
                  <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.07)', color: 'var(--text-3)', padding: '3px 9px', borderRadius: 100, border: '1px solid var(--border)' }}>
                    {order.order_source.toUpperCase()}
                  </span>
                )}
              </div>

              <div>
                <div className="rp-drawer-section-title">Order Details</div>
                <div className="rp-drawer-info-grid">
                  {[
                    ['Table',    order.table_label || '—'],
                    ['Type',     order.order_type || '—'],
                    ['Customer', order.customer_name || '—'],
                    ['Phone',    order.customer_phone || '—'],
                    ['Date',     fmtIST(order.created_at)],
                    ['GST',      `${order.gst_rate || 5}% (${order.gst_type || 'exclusive'})`],
                  ].map(([l, v]) => (
                    <div key={l} className="rp-drawer-info-item">
                      <span className="rp-drawer-info-label">{l}</span>
                      <span className="rp-drawer-info-val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {items.length > 0 && (
                <div>
                  <div className="rp-drawer-section-title">Items ({items.length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {items.map((it, i) => (
                      <div key={i} className="rp-drawer-item-row">
                        <div>
                          <div className="rp-drawer-item-name">
                            {it.name}{it.variant === 'half' ? ' ½' : ''} ×{it.qty}
                          </div>
                          {it.note && <div className="rp-drawer-item-meta">{it.note}</div>}
                        </div>
                        <div className="rp-drawer-item-price">{fmtD(it.total)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="rp-drawer-section-title">Bill</div>
                <div className="rp-drawer-totals">
                  <div className="rp-drawer-total-row"><span>Subtotal</span><span>{fmtD(order.subtotal)}</span></div>
                  {parseFloat(order.discount_amount) > 0 && (
                    <div className="rp-drawer-total-row" style={{ color: 'var(--mint)' }}>
                      <span>Discount{order.discount_type === 'percent' ? ` (${order.discount_value}%)` : ''}</span>
                      <span>−{fmtD(order.discount_amount)}</span>
                    </div>
                  )}
                  <div className="rp-drawer-total-row">
                    <span>GST {order.gst_rate}%</span>
                    <span>{fmtD(order.tax_amount)}</span>
                  </div>
                  <div className="rp-drawer-total-row grand">
                    <span>Grand Total</span>
                    <span>{fmtD(order.grand_total)}</span>
                  </div>
                </div>
              </div>

              {order.order_note && (
                <div style={{ background: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
                  📝 {order.order_note}
                </div>
              )}
              {order.cancelled_reason && (
                <div style={{ background: 'rgba(251,113,133,0.08)', border: '1px solid rgba(251,113,133,0.20)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--rose)', display: 'flex', gap: 8 }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }}/> {order.cancelled_reason}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   1. SALES TAB
───────────────────────────────────────────── */
function SalesTab() {
  const f       = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [page,    setPage]    = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await api().get('/reports/sales', { params: { ...f.params, page: p, limit: 30 } });
      setData(r.data); setPage(p);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params]);

  useEffect(() => { load(1); }, [f.params]); // eslint-disable-line

  const s = data?.summary || {};

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Analytics</div>
          <h2 className="rp-page-title">Sales <span>Report</span></h2>
          <p className="rp-page-sub">Revenue, orders and daily trends</p>
        </div>
        <PeriodBar {...f} onRefresh={() => load(1)} loading={loading}
          exportProps={{ endpoint: '/reports/export/sales', params: f.params, filename: 'sales-report' }}
        />
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/><p>Crunching numbers…</p></div>}
        {data && (
          <>
            <div className="rp-kpi-strip">
              {[
                { cls: 'k-revenue',  icon: <DollarSign size={16}/>,  label: 'Total Revenue',   val: fmt(s.total_revenue),    sub: `${fmtN(s.confirmed_orders)} confirmed` },
                { cls: 'k-orders',   icon: <ShoppingBag size={16}/>, label: 'Total Orders',    val: fmtN(s.total_orders),    sub: `${fmtN(s.cancelled_orders)} cancelled` },
                { cls: 'k-avg',      icon: <Zap size={16}/>,         label: 'Avg Order Value', val: fmt(s.avg_order_value),  sub: 'per confirmed order' },
                { cls: 'k-tax',      icon: <Receipt size={16}/>,     label: 'GST Collected',   val: fmt(s.total_tax),        sub: 'total tax amount' },
                { cls: 'k-discount', icon: <Star size={16}/>,        label: 'Discounts',       val: fmt(s.total_discount),   sub: 'total discount given' },
                { cls: 'k-cancel',   icon: <Ban size={16}/>,         label: 'Cancellations',   val: fmtN(s.cancelled_count), sub: 'cancelled orders' },
              ].map((k, i) => (
                <div key={i} className={`rp-kpi-card ${k.cls}`}>
                  <div className="rp-kpi-top"><div className="rp-kpi-icon">{k.icon}</div></div>
                  <div className="rp-kpi-label">{k.label}</div>
                  <div className="rp-kpi-value">{k.val}</div>
                  <div className="rp-kpi-sub">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="rp-two-col">
              <div className="rp-card">
                <div className="rp-card-header">
                  <div className="rp-card-title"><CreditCard size={14}/> Payment Methods</div>
                  <div className="rp-card-sub">{fmt(s.total_revenue)} total</div>
                </div>
                <div className="rp-card-body">
                  <DonutChart centerVal={fmt(s.total_revenue)} centerLabel="Revenue"
                    segments={[
                      { label: 'Cash',  value: s.cash_total  || 0, color: '#10b981' },
                      { label: 'Card',  value: s.card_total  || 0, color: '#38bdf8' },
                      { label: 'UPI',   value: s.upi_total   || 0, color: '#a78bfa' },
                      { label: 'Split', value: s.split_total || 0, color: '#f59e0b' },
                    ].filter(x => parseFloat(x.value) > 0)}
                  />
                </div>
              </div>

              <div className="rp-card">
                <div className="rp-card-header">
                  <div className="rp-card-title"><TrendingUp size={14}/> Daily Revenue</div>
                </div>
                <div className="rp-card-body">
                  {data.daily?.length > 0
                    ? <BarChart data={data.daily} valueKey="revenue" labelKey="date" maxBars={14}/>
                    : <div className="rp-empty"><TrendingUp size={24}/><p>No data for this period</p></div>
                  }
                </div>
              </div>
            </div>

            <div className="rp-card">
              <div className="rp-card-header">
                <div className="rp-card-title"><ShoppingBag size={14}/> Orders ({fmtN(data.pagination?.total)})</div>
                <div className="rp-card-sub">Click any row to view details</div>
              </div>
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead>
                    <tr><th>#</th><th>Table</th><th>Customer</th><th>Type</th><th>Source</th><th>Status</th><th>Payment</th><th>Total</th><th>Date (IST)</th></tr>
                  </thead>
                  <tbody>
                    {(data.orders || []).map(o => (
                      <tr key={o.id} className="clickable" onClick={() => setOrderId(o.id)}>
                        <td className="td-num">{o.order_number}</td>
                        <td style={{ fontWeight: 600 }}>{o.table_label || '—'}</td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{o.customer_name || '—'}</div>
                          {o.customer_phone && <div style={{ fontSize: 11, color: 'var(--text-4)', fontFamily: 'var(--f-mono)' }}>{o.customer_phone}</div>}
                        </td>
                        <td className="td-muted" style={{ textTransform: 'capitalize' }}>{o.order_type}</td>
                        <td><span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.07)', color: 'var(--text-3)', padding: '2px 7px', borderRadius: 100, border: '1px solid var(--border)' }}>{o.order_source || 'pos'}</span></td>
                        <td><span className={`rp-badge ${STATUS_META[o.status]?.cls || ''}`}>{STATUS_META[o.status]?.label || o.status}</span></td>
                        <td><span className={`rp-pay-badge ${o.payment_method}`}>{o.payment_method?.toUpperCase()}</span></td>
                        <td className="td-money">{fmt(o.grand_total)}</td>
                        <td><span className="rp-date-ist">{fmtIST(o.created_at, true)}</span></td>
                      </tr>
                    ))}
                    {(data.orders || []).length === 0 && (
                      <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-4)' }}>No orders found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data.pagination && (
                <div className="rp-pagination">
                  <span className="rp-pagination-info">
                    Page {page} / {data.pagination.pages} · {fmtN(data.pagination.total)} orders
                  </span>
                  <div className="rp-pagination-btns">
                    <button className="rp-page-btn" disabled={page <= 1} onClick={() => load(page - 1)}><ChevronLeft size={14}/></button>
                    <button className="rp-page-btn" disabled={page >= data.pagination.pages} onClick={() => load(page + 1)}><ChevronRight size={14}/></button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {orderId && <OrderDrawer orderId={orderId} onClose={() => setOrderId(null)}/>}
    </>
  );
}

/* ─────────────────────────────────────────────
   2. ORDERS TAB
───────────────────────────────────────────── */
function OrdersTab() {
  const f = useFilters();
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [orderId,       setOrderId]       = useState(null);
  const [page,          setPage]          = useState(1);
  const [search,        setSearch]        = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterType,    setFilterType]    = useState('');
  const [filterSource,  setFilterSource]  = useState('');
  const [filterPayment, setFilterPayment] = useState('');

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await api().get('/reports/orders', {
        params: {
          ...f.params, page: p, limit: 40,
          ...(search        && { search }),
          ...(filterStatus  && { status: filterStatus }),
          ...(filterType    && { order_type: filterType }),
          ...(filterSource  && { order_source: filterSource }),
          ...(filterPayment && { payment_method: filterPayment }),
        },
      });
      setData(r.data); setPage(p);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params, search, filterStatus, filterType, filterSource, filterPayment]);

  useEffect(() => { load(1); }, [f.params, filterStatus, filterType, filterSource, filterPayment]); // eslint-disable-line

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Records</div>
          <h2 className="rp-page-title">All <span>Orders</span></h2>
          <p className="rp-page-sub">Complete order history with advanced filters</p>
        </div>
        <PeriodBar {...f} onRefresh={() => load(1)} loading={loading}
          exportProps={{ endpoint: '/reports/export/orders', params: { ...f.params, status: filterStatus, order_type: filterType, order_source: filterSource, payment_method: filterPayment }, filename: 'orders' }}
        />
      </div>

      <div className="rp-filters">
        <span className="rp-filter-label">Filter</span>
        <div className="rp-search-box">
          <Search size={13}/>
          <input placeholder="Order #, customer name, phone…"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(1)}/>
        </div>
        {[
          { val: filterStatus,  set: setFilterStatus,  opts: [['','All Status'],['placed','Placed'],['kot','KOT'],['preparing','Preparing'],['ready','Ready'],['served','Served'],['cancelled','Cancelled']] },
          { val: filterType,    set: setFilterType,    opts: [['','All Types'],['table','Table'],['parcel','Parcel'],['zomato','Zomato'],['swiggy','Swiggy']] },
          { val: filterSource,  set: setFilterSource,  opts: [['','All Sources'],['pos','POS'],['qr','QR'],['online','Online']] },
          { val: filterPayment, set: setFilterPayment, opts: [['','All Payment'],['cash','Cash'],['card','Card'],['upi','UPI'],['split','Split']] },
        ].map((f2, i) => (
          <select key={i} className="rp-filter-select" value={f2.val} onChange={e => f2.set(e.target.value)}>
            {f2.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
      </div>

      <div className="rp-body" style={{ paddingTop: 12 }}>
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <div className="rp-card">
            <div className="rp-card-header">
              <div className="rp-card-title"><ShoppingBag size={14}/> {fmtN(data.pagination?.total)} Orders</div>
            </div>
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr><th>#</th><th>Table</th><th>Customer</th><th>Type</th><th>Source</th><th>Status</th><th>Payment</th><th>Items</th><th>Total</th><th>Date (IST)</th></tr>
                </thead>
                <tbody>
                  {(data.orders || []).length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-4)' }}>No orders match filters</td></tr>
                  )}
                  {(data.orders || []).map(o => {
                    const itms = (() => { try { return typeof o.items === 'string' ? JSON.parse(o.items || '[]') : (o.items || []); } catch { return []; } })();
                    return (
                      <tr key={o.id} className="clickable" onClick={() => setOrderId(o.id)}>
                        <td className="td-num">{o.order_number}</td>
                        <td style={{ fontWeight: 600 }}>{o.table_label || '—'}</td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{o.customer_name || '—'}</div>
                          {o.customer_phone && <div style={{ fontSize: 11, color: 'var(--text-4)', fontFamily: 'var(--f-mono)' }}>{o.customer_phone}</div>}
                        </td>
                        <td className="td-muted" style={{ textTransform: 'capitalize' }}>{o.order_type}</td>
                        <td><span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.07)', color: 'var(--text-3)', padding: '2px 7px', borderRadius: 100, border: '1px solid var(--border)' }}>{o.order_source || 'pos'}</span></td>
                        <td><span className={`rp-badge ${STATUS_META[o.status]?.cls || ''}`}>{STATUS_META[o.status]?.label || o.status}</span></td>
                        <td><span className={`rp-pay-badge ${o.payment_method}`}>{o.payment_method?.toUpperCase()}</span></td>
                        <td className="td-muted">{itms.length} items</td>
                        <td className="td-money">{fmt(o.grand_total)}</td>
                        <td><span className="rp-date-ist">{fmtIST(o.created_at, true)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data.pagination && (
              <div className="rp-pagination">
                <span className="rp-pagination-info">Page {page} / {Math.max(data.pagination.pages, 1)} · {fmtN(data.pagination.total)} orders</span>
                <div className="rp-pagination-btns">
                  <button className="rp-page-btn" disabled={page <= 1} onClick={() => load(page - 1)}><ChevronLeft size={14}/></button>
                  <button className="rp-page-btn" disabled={page >= data.pagination.pages} onClick={() => load(page + 1)}><ChevronRight size={14}/></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {orderId && <OrderDrawer orderId={orderId} onClose={() => setOrderId(null)}/>}
    </>
  );
}

/* ─────────────────────────────────────────────
   3. ITEMS TAB
───────────────────────────────────────────── */
function ItemsTab() {
  const f = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort,    setSort]    = useState('qty');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/reports/items', { params: { ...f.params, sort, limit: 50 } });
      setData(r.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params, sort]);

  useEffect(() => { load(); }, [f.params, sort]); // eslint-disable-line

  const s      = data?.summary || {};
  const maxQty = Math.max(...(data?.items || []).map(i => parseInt(i.qty_sold || 0)), 1);

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Menu Analytics</div>
          <h2 className="rp-page-title">Item <span>Sales</span></h2>
          <p className="rp-page-sub">Best sellers ranked by quantity or revenue</p>
        </div>
        <PeriodBar {...f} onRefresh={load} loading={loading}
          extra={
            <div className="rp-period-pills">
              <button className={`rp-period-btn ${sort === 'qty' ? 'active' : ''}`} onClick={() => setSort('qty')}>By Qty</button>
              <button className={`rp-period-btn ${sort === 'revenue' ? 'active' : ''}`} onClick={() => setSort('revenue')}>By ₹</button>
            </div>
          }
          exportProps={{ endpoint: '/reports/export/items', params: { ...f.params, sort }, filename: 'item-sales' }}
        />
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <>
            <div className="rp-kpi-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { cls: 'k-orders',  icon: <Package size={16}/>,     label: 'Items Sold',    val: fmtN(s.total_qty_sold),    sub: 'total units' },
                { cls: 'k-revenue', icon: <DollarSign size={16}/>,  label: 'Item Revenue',  val: fmt(s.total_item_revenue), sub: 'before tax' },
                { cls: 'k-avg',     icon: <Star size={16}/>,        label: 'Unique Items',  val: fmtN(s.unique_items),      sub: 'distinct items sold' },
              ].map((k, i) => (
                <div key={i} className={`rp-kpi-card ${k.cls}`}>
                  <div className="rp-kpi-top"><div className="rp-kpi-icon">{k.icon}</div></div>
                  <div className="rp-kpi-label">{k.label}</div>
                  <div className="rp-kpi-value">{k.val}</div>
                  <div className="rp-kpi-sub">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="rp-two-col">
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><PieChart size={14}/> By Category</div></div>
                <div className="rp-card-body">
                  {(data.categories || []).map((c, i) => (
                    <div key={i} className="rp-summary-row">
                      <div className="rp-summary-left">
                        <div className="rp-summary-dot" style={{ background: ['#f59e0b','#38bdf8','#10b981','#a78bfa','#fb7185'][i % 5] }}/>
                        <div>
                          <div className="rp-summary-label">{c.category || 'Uncategorised'}</div>
                          <div className="rp-summary-count">{fmtN(c.qty_sold)} units</div>
                        </div>
                      </div>
                      <div className="rp-summary-amount">{fmt(c.revenue)}</div>
                    </div>
                  ))}
                  {(!data.categories || data.categories.length === 0) && <div className="rp-empty"><Package size={24}/><p>No data</p></div>}
                </div>
              </div>

              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><TrendingUp size={14}/> Top 8 by {sort === 'qty' ? 'Quantity' : 'Revenue'}</div></div>
                <div className="rp-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(data.items || []).slice(0, 8).map((item, i) => (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{item.name}{item.variant === 'half' ? ' ½' : ''}</span>
                        <span style={{ fontWeight: 600, fontFamily: 'var(--f-mono)', color: 'var(--amber)', fontSize: 11 }}>
                          {sort === 'qty' ? `${fmtN(item.qty_sold)} sold` : fmt(item.revenue)}
                        </span>
                      </div>
                      <div className="rp-progress">
                        <div className="rp-progress-bg">
                          <div className="rp-progress-fill" style={{ width: `${pct(parseInt(item.qty_sold), maxQty)}%` }}/>
                        </div>
                        <span className="rp-progress-val">{pct(parseInt(item.qty_sold), maxQty)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rp-card">
              <div className="rp-card-header"><div className="rp-card-title"><Package size={14}/> All Items ({data.items?.length})</div></div>
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead><tr><th>Item</th><th>Category</th><th>Variant</th><th>Orders</th><th>Qty Sold</th><th>Avg Price</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {(data.items || []).map((item, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, color: 'var(--text-1)' }}>{item.name}</td>
                        <td className="td-muted">{item.category || '—'}</td>
                        <td><span style={{ fontSize: 10, fontWeight: 700, background: item.variant === 'half' ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.07)', color: item.variant === 'half' ? 'var(--amber)' : 'var(--text-3)', padding: '2px 7px', borderRadius: 100, border: '1px solid var(--border)' }}>{item.variant}</span></td>
                        <td className="td-muted">{fmtN(item.order_count)}</td>
                        <td className="td-num">{fmtN(item.qty_sold)}</td>
                        <td className="td-muted">{fmtD(item.avg_price)}</td>
                        <td className="td-money">{fmt(item.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   4. TABLES TAB
───────────────────────────────────────────── */
function TablesTab() {
  const f = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/reports/tables', { params: f.params });
      setData(r.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params]);

  useEffect(() => { load(); }, [f.params]); // eslint-disable-line

  const maxRev = Math.max(...(data?.tables || []).map(t => parseFloat(t.total_revenue || 0)), 1);

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Venue Analytics</div>
          <h2 className="rp-page-title">Table <span>Performance</span></h2>
          <p className="rp-page-sub">Revenue and turn statistics per table</p>
        </div>
        <PeriodBar {...f} onRefresh={load} loading={loading}/>
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <>
            {data.tables?.length > 0 && (
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><TrendingUp size={14}/> Revenue by Table</div></div>
                <div className="rp-card-body">
                  <BarChart data={data.tables.slice(0, 12)} valueKey="total_revenue" labelKey="table_label" maxBars={12}/>
                </div>
              </div>
            )}

            <div className="rp-card">
              <div className="rp-card-header">
                <div className="rp-card-title"><TableProperties size={14}/> Table Stats ({data.tables?.length})</div>
              </div>
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead><tr><th>Table</th><th>Orders</th><th>Total Revenue</th><th>Avg Bill</th><th>Highest Bill</th><th>Cancellations</th><th>Share</th></tr></thead>
                  <tbody>
                    {(data.tables || []).map((t, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 800, color: 'var(--amber)', fontFamily: 'var(--f-mono)' }}>{t.table_label}</td>
                        <td className="td-num">{fmtN(t.total_orders)}</td>
                        <td className="td-money">{fmt(t.total_revenue)}</td>
                        <td className="td-muted">{fmt(t.avg_bill)}</td>
                        <td className="td-muted">{fmt(t.highest_bill)}</td>
                        <td className="td-muted">{fmtN(t.cancellations)}</td>
                        <td style={{ minWidth: 120 }}>
                          <div className="rp-progress">
                            <div className="rp-progress-bg">
                              <div className="rp-progress-fill" style={{ width: `${pct(t.total_revenue, maxRev)}%` }}/>
                            </div>
                            <span className="rp-progress-val">{pct(t.total_revenue, maxRev)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   5. PAYMENTS TAB
───────────────────────────────────────────── */
function PaymentsTab() {
  const f = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/reports/payments', { params: f.params });
      setData(r.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params]);

  useEffect(() => { load(); }, [f.params]); // eslint-disable-line

  const s = data?.summary || {};
  const payBreakdown = [
    { key: 'cash',  label: 'Cash',  icon: <Banknote size={14}/>,              amount: s.cash,  orders: s.cash_orders,  color: '#10b981' },
    { key: 'card',  label: 'Card',  icon: <CreditCard size={14}/>,            amount: s.card,  orders: s.card_orders,  color: '#38bdf8' },
    { key: 'upi',   label: 'UPI',   icon: <Smartphone size={14}/>,            amount: s.upi,   orders: s.upi_orders,   color: '#a78bfa' },
    { key: 'split', label: 'Split', icon: <SplitSquareHorizontal size={14}/>, amount: s.split, orders: s.split_orders, color: '#f59e0b' },
  ];

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Financial</div>
          <h2 className="rp-page-title">Payment <span>Report</span></h2>
          <p className="rp-page-sub">Cash, card, UPI and split payment breakdown</p>
        </div>
        <PeriodBar {...f} onRefresh={load} loading={loading}
          exportProps={{ endpoint: '/reports/export/payments', params: f.params, filename: 'payments' }}
        />
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <>
            <div className="rp-kpi-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {payBreakdown.map((p, i) => (
                <div key={i} className="rp-kpi-card" style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${p.color}, ${p.color}88)`, borderRadius: '2px 2px 0 0' }}/>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${p.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: p.color }}>{p.icon}</div>
                  <div className="rp-kpi-label">{p.label}</div>
                  <div className="rp-kpi-value" style={{ fontSize: 22 }}>{fmt(p.amount)}</div>
                  <div className="rp-kpi-sub">{fmtN(p.orders)} orders</div>
                </div>
              ))}
            </div>

            <div className="rp-two-col">
              <div className="rp-card">
                <div className="rp-card-header">
                  <div className="rp-card-title"><PieChart size={14}/> Collection Split</div>
                  <div className="rp-card-sub">{fmt(s.total_collected)} total</div>
                </div>
                <div className="rp-card-body">
                  <DonutChart centerVal={fmt(s.total_collected)} centerLabel="Collected"
                    segments={payBreakdown.filter(p => parseFloat(p.amount) > 0)
                      .map(p => ({ label: p.label, value: p.amount || 0, color: p.color }))}
                  />
                </div>
              </div>

              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><TrendingUp size={14}/> Daily Collection</div></div>
                <div className="rp-card-body">
                  {data.daily?.length > 0
                    ? <BarChart data={data.daily} valueKey="total" labelKey="date" maxBars={14}/>
                    : <div className="rp-empty"><TrendingUp size={24}/><p>No data</p></div>
                  }
                </div>
              </div>
            </div>

            {parseFloat(s.split) > 0 && (
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><SplitSquareHorizontal size={14}/> Split Payment Detail</div></div>
                <div className="rp-card-body">
                  {[['Cash portion', s.split_cash, '#10b981'], ['Card portion', s.split_card, '#38bdf8'], ['UPI portion', s.split_upi, '#a78bfa']].map(([l, v, c]) => (
                    <div key={l} className="rp-summary-row">
                      <div className="rp-summary-left">
                        <div className="rp-summary-dot" style={{ background: c }}/>
                        <span className="rp-summary-label">{l}</span>
                      </div>
                      <span className="rp-summary-amount" style={{ color: c }}>{fmt(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   6. GST TAB
───────────────────────────────────────────── */
function GstTab() {
  const f = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/reports/gst', { params: f.params });
      setData(r.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params]);

  useEffect(() => { load(); }, [f.params]); // eslint-disable-line

  const s = data?.summary || {};

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Compliance</div>
          <h2 className="rp-page-title">GST / <span>Tax Report</span></h2>
          <p className="rp-page-sub">Tax collected by rate, type and period</p>
        </div>
        <PeriodBar {...f} onRefresh={load} loading={loading}
          exportProps={{ endpoint: '/reports/export/gst', params: f.params, filename: 'gst-report' }}
        />
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <>
            <div className="rp-kpi-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {[
                { cls: 'k-tax',     icon: <Receipt size={16}/>,     label: 'GST Collected', val: fmt(s.total_gst),     sub: 'all rates combined' },
                { cls: 'k-revenue', icon: <DollarSign size={16}/>,  label: 'Gross Revenue', val: fmt(s.gross_revenue), sub: 'incl. tax' },
                { cls: 'k-avg',     icon: <TrendingUp size={16}/>,  label: 'Net Revenue',   val: fmt(s.net_revenue),   sub: 'excl. tax' },
                { cls: 'k-orders',  icon: <ShoppingBag size={16}/>, label: 'Orders',        val: fmtN(s.total_orders), sub: 'taxable orders' },
              ].map((k, i) => (
                <div key={i} className={`rp-kpi-card ${k.cls}`}>
                  <div className="rp-kpi-top"><div className="rp-kpi-icon">{k.icon}</div></div>
                  <div className="rp-kpi-label">{k.label}</div>
                  <div className="rp-kpi-value">{k.val}</div>
                  <div className="rp-kpi-sub">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="rp-two-col">
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><Receipt size={14}/> By GST Rate</div></div>
                <div className="rp-card-body">
                  {(data.by_rate || []).map((r, i) => (
                    <div key={i} className="rp-summary-row">
                      <div className="rp-summary-left">
                        <div className="rp-summary-dot" style={{ background: ['#f59e0b','#38bdf8','#10b981','#a78bfa'][i % 4] }}/>
                        <div>
                          <div className="rp-summary-label">{r.gst_rate}% — {r.gst_type}</div>
                          <div className="rp-summary-count">{fmtN(r.orders)} orders · taxable: {fmt(r.taxable_amount)}</div>
                        </div>
                      </div>
                      <div className="rp-summary-amount">{fmt(r.gst_collected)}</div>
                    </div>
                  ))}
                  {(!data.by_rate || data.by_rate.length === 0) && <div className="rp-empty"><Receipt size={24}/><p>No GST data</p></div>}
                </div>
              </div>

              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><TrendingUp size={14}/> Daily GST</div></div>
                <div className="rp-card-body">
                  {data.daily?.length > 0
                    ? <BarChart data={data.daily} valueKey="gst_collected" labelKey="date" maxBars={14}/>
                    : <div className="rp-empty"><Receipt size={24}/><p>No data</p></div>
                  }
                </div>
              </div>
            </div>

            {data.daily?.length > 0 && (
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><FileText size={14}/> Daily GST Summary</div></div>
                <div className="rp-table-wrap">
                  <table className="rp-table">
                    <thead><tr><th>Date (IST)</th><th>Taxable Amount</th><th>GST Collected</th><th>Gross Total</th></tr></thead>
                    <tbody>
                      {data.daily.map((d, i) => (
                        <tr key={i}>
                          <td><span className="rp-date-ist">{fmtIST(d.date, true)}</span></td>
                          <td className="td-num">{fmt(d.taxable_amount)}</td>
                          <td className="td-money">{fmt(d.gst_collected)}</td>
                          <td className="td-muted">{fmt(d.gross_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   7. CANCELLATIONS TAB
───────────────────────────────────────────── */
function CancellationsTab() {
  const f = useFilters();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/reports/cancellations', { params: f.params });
      setData(r.data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [f.params]);

  useEffect(() => { load(); }, [f.params]); // eslint-disable-line

  const s = data?.summary || {};

  return (
    <>
      <div className="rp-header">
        <div className="rp-header-left">
          <div className="rp-page-eyebrow">Loss Analysis</div>
          <h2 className="rp-page-title">Cancellation <span>Report</span></h2>
          <p className="rp-page-sub">Cancelled orders, lost revenue and common reasons</p>
        </div>
        <PeriodBar {...f} onRefresh={load} loading={loading}
          exportProps={{ endpoint: '/reports/export/cancellations', params: f.params, filename: 'cancellations' }}
        />
      </div>

      <div className="rp-body">
        {loading && !data && <div className="rp-loading"><Loader2 size={28} className="rp-spin"/></div>}
        {data && (
          <>
            <div className="rp-kpi-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { cls: 'k-cancel',  icon: <Ban size={16}/>,        label: 'Cancelled Orders',   val: fmtN(s.total_cancelled),    sub: 'total cancellations' },
                { cls: 'k-revenue', icon: <DollarSign size={16}/>, label: 'Lost Revenue',        val: fmt(s.lost_revenue),        sub: 'potential revenue lost' },
                { cls: 'k-avg',     icon: <Zap size={16}/>,        label: 'Avg Cancelled Value', val: fmt(s.avg_cancelled_value), sub: 'per cancelled order' },
              ].map((k, i) => (
                <div key={i} className={`rp-kpi-card ${k.cls}`}>
                  <div className="rp-kpi-top"><div className="rp-kpi-icon">{k.icon}</div></div>
                  <div className="rp-kpi-label">{k.label}</div>
                  <div className="rp-kpi-value">{k.val}</div>
                  <div className="rp-kpi-sub">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="rp-two-col">
              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><AlertTriangle size={14}/> By Reason</div></div>
                <div className="rp-card-body">
                  {(data.by_reason || []).map((r, i) => (
                    <div key={i} className="rp-summary-row">
                      <div className="rp-summary-left">
                        <div className="rp-summary-dot" style={{ background: 'var(--rose)' }}/>
                        <div>
                          <div className="rp-summary-label" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</div>
                          <div className="rp-summary-count">{fmtN(r.count)} orders</div>
                        </div>
                      </div>
                      <div className="rp-summary-amount" style={{ color: 'var(--rose)' }}>−{fmt(r.lost_revenue)}</div>
                    </div>
                  ))}
                  {(!data.by_reason || data.by_reason.length === 0) && <div className="rp-empty"><Ban size={24}/><p>No cancellations</p></div>}
                </div>
              </div>

              <div className="rp-card">
                <div className="rp-card-header"><div className="rp-card-title"><FileText size={14}/> Recent Cancellations</div></div>
                <div className="rp-table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
                  <table className="rp-table">
                    <thead><tr><th>#</th><th>Table</th><th>Customer</th><th>Lost ₹</th><th>Reason</th><th>Date</th></tr></thead>
                    <tbody>
                      {(data.orders || []).slice(0, 20).map((o, i) => (
                        <tr key={i}>
                          <td className="td-num">{o.order_number}</td>
                          <td style={{ fontWeight: 600 }}>{o.table_label || '—'}</td>
                          <td className="td-muted">{o.customer_name || '—'}</td>
                          <td style={{ color: 'var(--rose)', fontFamily: 'var(--f-mono)', fontSize: 12 }}>−{fmt(o.grand_total)}</td>
                          <td className="td-muted" style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{o.cancelled_reason || '—'}</td>
                          <td><span className="rp-date-ist">{fmtIST(o.created_at, true)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */
function Reports() {
  const [activeTab, setActiveTab] = useState('sales');

  const TAB_MAP = {
    sales:         <SalesTab />,
    orders:        <OrdersTab />,
    items:         <ItemsTab />,
    tables:        <TablesTab />,
    payments:      <PaymentsTab />,
    gst:           <GstTab />,
    cancellations: <CancellationsTab />,
  };

  return (
    <div className="rp-root">
      <Navbar />
      <div className="rp-main">
        <nav className="rp-nav">
          <div className="rp-nav-section">Reports</div>
          {NAV_ITEMS.map(n => (
            <button key={n.id} className={`rp-nav-btn ${activeTab === n.id ? 'active' : ''}`}
              onClick={() => setActiveTab(n.id)}>
              {n.icon}
              <span>{n.label}</span>
            </button>
          ))}
        </nav>

        <div className="rp-content">
          {TAB_MAP[activeTab] || null}
        </div>
      </div>
    </div>
  );
}

export default Reports;
